import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFeature } from "flagged";
import GridLayout from "./GridLayout";
import {
  layout as layoutModel,
  layout as layoutRepository,
} from "@/repositories/layout";
import { useStore } from "@/stores/useStore";
import { Container, Layout, LayoutAccess } from "@/types/v2";
import RGL from "react-grid-layout";
import clone from "just-clone";
import { Button } from "@/components/Base/Button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/Base/Select";
import { LayoutAccessDialog } from "@/components/Dialogs/LayoutAccessDialog";
import { LayoutNameDialog } from "@/components/Dialogs/LayoutNameDialog";
import { showErrorToast, showSuccessToast } from "@/lib/toast";
import {
  Trash2,
  PlusIcon,
  AppWindowMacIcon,
  SaveIcon,
  LayoutTemplateIcon,
  ListChecks,
} from "lucide-react";
import { Tooltip } from "@/components/Common/Tooltip";
import { container as containerRepository } from "@/repositories/container";
import { Link, useSearchParams } from "react-router-dom";
import { Label } from "@/components/Base/Label";
import { ContainerDialog } from "@/components/Layout/ContainerDialog";
import { useConfirmDialog } from "@/components/Common/ConfirmDialog";
import { SupportLink } from "@/components/Common/SupportLink";
import { ImportExportLayout } from "@/components/Layout/ImportExportLayout";
import { MultiSelect } from "@/components/Base/MultiSelect";
import { Skeleton } from "@/components/Base/Skeleton";
import {
  useLayoutAccessQuery,
  useUpdateLayoutAccessMutation,
  useInvalidateLayoutAccessQuery,
} from "@/hooks/queries/useLayoutAccessQuery";
import { getIntegrationName } from "@/lib/integration";
import { isTechnicianWritebackEnabledForShop } from "@/lib/technicianWriteback";
import { getLinkableLabels } from "@/lib/getLinkableLabels";
import { toggleContainerSelection } from "@/lib/containerSelection";
import { BulkEditActionBar } from "@/components/Layout/BulkEditActionBar";
import { BulkLinkDialog } from "@/components/Layout/BulkLinkDialog";
import { useBulkLinkEditAvailability } from "@/hooks/useBulkLinkEditAvailability";

export const LayoutCreator = () => {
  const { confirm, Dialog } = useConfirmDialog();

  const {
    shop: shopData,
    employees,
    dashboardLayoutId,
    setDashboardLayoutId,
  } = useStore();
  const [isEditing, setIsEditing] = useState(true);

  const labels = useMemo(
    () => (shopData ? getLinkableLabels(shopData) : []),
    [shopData]
  );
  const layouts = useMemo(() => shopData?.layouts ?? [], [shopData]);
  const integrationName = getIntegrationName(shopData);
  const technicianWritebackEnabled = shopData
    ? isTechnicianWritebackEnabledForShop(shopData)
    : false;

  const [searchParams, setSearchParams] = useSearchParams();

  const [layout, setLayout] = useState<Layout[] | RGL.Layout[]>(
    layoutModel.generateBaseLayout()
  );
  const shopId = useStore((s) => s.shopId);

  const [newLayoutName, setNewLayoutName] = useState(
    searchParams.get("layout") || ""
  );

  const [layoutAccess, setLayoutAccess] = useState<LayoutAccess>();
  // Bulk Edit has two modes. With the bulk-link rollout on, the bar offers
  // "Edit links…" (BulkLinkDialog, WEBV3-342), gated by the same hook the
  // dialog gates itself on so button and dialog can't disagree. While it is
  // off, Bulk Edit is the legacy label-only apply, gated on
  // isROLabelLinkEnabled like the per-tile edit dialog's label section. Those
  // are two flag stores (global `flagged` vs shop.enabledFeatures), so until
  // the legacy path is removed the toggle can still show via the global label
  // flag even when the hook reports both shop-level link flags off.
  const isROLabelLinkEnabled = !!useFeature("isROLabelLinkEnabled");
  const { enabled: isBulkLinkEditAvailable } = useBulkLinkEditAvailability();
  const isBulkEditAvailable = isBulkLinkEditAvailable || isROLabelLinkEnabled;
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);
  const [isApplyingBulkLabel, setIsApplyingBulkLabel] = useState(false);
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);

  const currentLayoutContainers = useMemo(() => {
    return layout.map((container) => container.i);
  }, [layout]);

  // `selection` can go stale relative to `layout`: the
  // "Add Existing Containers" MultiSelect stays interactive during
  // bulk-edit mode (only react-grid-layout's own drag/resize is disabled),
  // so deselecting a container there removes it from `layout` immediately
  // via handleContainersChange -> removeItem, without touching `selection`.
  // Without filtering, the displayed selected count and the actual
  // `handleApplyBulkLabel` write target could diverge -- e.g. showing
  // "3 selected" while writing a label to a container no longer visible on
  // the canvas. Deriving this view keeps display and writes from ever
  // disagreeing, without needing `selection` itself to be pruned eagerly.
  // See WEBV3-336 review.
  const selectedInLayout = useMemo(
    () => selection.filter((id) => currentLayoutContainers.includes(id)),
    [selection, currentLayoutContainers]
  );

  useEffect(() => {
    setSelection([]);
  }, [newLayoutName]);

  // The flags can flip off while the page is open; leave bulk mode (and drop
  // its selection) so it doesn't linger with no way to exit it.
  useEffect(() => {
    if (isBulkEditAvailable) return;
    setBulkEditMode(false);
    setSelection([]);
  }, [isBulkEditAvailable]);

  // The dialog only stays open while it can be used: closing here (rather
  // than in each handler) also covers exit, clear, layout switch and a flag
  // flipping off, so a later flag flip back on can't pop it open by itself.
  useEffect(() => {
    if (
      !isBulkLinkEditAvailable ||
      !bulkEditMode ||
      selectedInLayout.length === 0
    ) {
      setBulkLinkOpen(false);
    }
  }, [isBulkLinkEditAvailable, bulkEditMode, selectedInLayout.length]);

  const sortedContainerOptions = useMemo(() => {
    const options = (shopData?.containers ?? []).map((c) => ({
      value: c.id,
      label: c.displayName ?? c.id,
    }));

    return options.sort((a, b) => {
      const aSelected = currentLayoutContainers.includes(a.value);
      const bSelected = currentLayoutContainers.includes(b.value);
      if (aSelected !== bSelected) return aSelected ? -1 : 1;
      return a.label.localeCompare(b.label, undefined, {
        sensitivity: "base",
      });
    });
  }, [shopData?.containers, currentLayoutContainers]);

  // Use React Query for independent layout access fetching
  const { data: layoutAccessArray = [] } = useLayoutAccessQuery(shopId);
  const updateLayoutAccessMutation = useUpdateLayoutAccessMutation(shopId);
  const invalidateLayoutAccessQuery = useInvalidateLayoutAccessQuery(shopId);

  const [localLayouts, setLocalLayouts] = useState(layouts);

  useEffect(() => {
    if (shopData?.layouts) {
      if (
        JSON.stringify(shopData.layouts) !== JSON.stringify(localLayouts) &&
        !isEditing
      ) {
        console.log("Updating local layouts");
        setLocalLayouts(shopData.layouts);
      }
    }
  }, [localLayouts, shopData?.layouts, isEditing]);

  // Tracks a layout name we just optimistically switched to (via
  // updateLayoutParam) that may not exist in `layouts` yet -- Firestore's
  // realtime listener can take a render or two to deliver a just-created
  // layout after an arrayUnion write resolves. Without this, the fallback
  // effect below sees `layoutParam` missing from `layoutNames` and falls
  // back to `layoutNames[0]`, silently reverting a freshly duplicated (or
  // newly created, or imported) layout back to whichever layout happened
  // to be first in the array. See WEBV3-230.
  const pendingLayoutNameRef = useRef<string | null>(null);

  useEffect(() => {
    const layoutParam = searchParams.get("layout");
    const layoutNames = layouts.map((layout) => Object.keys(layout)[0]);

    if (!layoutParam) return;

    if (layoutNames.includes(layoutParam)) {
      if (pendingLayoutNameRef.current === layoutParam) {
        pendingLayoutNameRef.current = null;
      }
      // React Router's setSearchParams doesn't land in the same commit as
      // the setNewLayoutName call in updateLayoutParam below, so this
      // effect can re-run (it depends on newLayoutName) against a
      // `searchParams` that hasn't caught up yet -- `layoutParam` here is
      // still the *previous* layout name, which is trivially a valid
      // match, so without the pending guard this branch "corrects"
      // newLayoutName right back to the layout we just switched away
      // from. Confirmed live in the browser: WEBV3-230.
      if (
        layoutParam !== newLayoutName &&
        pendingLayoutNameRef.current === null
      ) {
        setNewLayoutName(layoutParam);
      }
      return;
    }

    if (pendingLayoutNameRef.current === layoutParam) {
      // We just switched to this name ourselves (see updateLayoutParam)
      // and `layouts` hasn't caught up yet. Wait for it instead of
      // falling back -- falling back here is what caused WEBV3-230.
      return;
    }

    const fallbackName = layoutNames[0] || "";
    if (fallbackName !== newLayoutName) {
      setNewLayoutName(fallbackName);
    }
  }, [searchParams, layouts, newLayoutName]);

  const updateLayoutParam = useCallback(
    (layoutId: string | null) => {
      if (layoutId) {
        pendingLayoutNameRef.current = layoutId;
        setNewLayoutName(layoutId); // Update local state immediately

        // Update URL params
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.set("layout", layoutId);
            return newParams;
          },
          { replace: true }
        ); // Use replace to avoid adding to history stack
      } else {
        pendingLayoutNameRef.current = null;
        setNewLayoutName("");
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev);
            newParams.delete("layout");
            return newParams;
          },
          { replace: true }
        );
      }
    },
    [setSearchParams]
  );

  const handleContainersChange = (containers: string[]) => {
    const currentContainers = layout.map((l) => toLayout(l)).map((l) => l.i);
    const containersToAdd = containers.filter(
      (c) => !currentContainers.includes(c)
    );
    const containersToRemove = currentContainers.filter(
      (c) => !containers.includes(c)
    );
    containersToAdd.forEach((c) => handleAddContainerToLayout(c));
    containersToRemove.forEach((c) => removeItem(c));
  };

  const handleAddContainerToLayout = async (id: string) => {
    const newLayout = layoutRepository.addContainerToLayout(
      id,
      layout.map((l) => toLayout(l))
    );
    setLayout(newLayout);
  };

  const removeItem = (id: string) => {
    const layoutCopy = layout.slice();
    const newLayout = layoutCopy.filter((item) => item.i !== id);
    setLayout(newLayout);
  };

  const toLayout = (item: RGL.Layout): Layout => {
    const validEntries = Object.entries(item).filter(
      ([, value]) => value !== undefined
    );
    return Object.fromEntries(validEntries) as Layout;
  };

  const onLayoutChange = (layout: RGL.Layout[]) => {
    setLayout(layout.map((l) => toLayout(l)));
  };

  const handleSelectLayout = (layoutName: string) => {
    if (layoutName === "placeholder") return; // Ignore placeholder selection
    updateLayoutParam(layoutName);
  };

  const updateLayout = useCallback(
    (layoutName: string, layoutAccessArr: LayoutAccess[] | undefined) => {
      if (layoutName !== "") {
        if (layoutName === "placeholder") return; // Ignore placeholder selection
        const layoutIndex = localLayouts.findIndex(
          (layout) => Object.keys(layout)[0] === layoutName
        );
        if (layoutIndex < 0) return;
        const newLayoutObj = localLayouts[layoutIndex];
        const keys = Object.keys(newLayoutObj);
        const layout = newLayoutObj[keys[0]];
        setLayout(layout);
        setLayoutAccess(
          layoutAccessArr?.find((access) => access.layoutName === layoutName)
        );
      }
    },
    [localLayouts]
  );

  useEffect(() => {
    console.log("Updating layout");
    updateLayout(newLayoutName, layoutAccessArray);
  }, [layoutAccessArray, newLayoutName, updateLayout]);

  const handleLayoutDelete = async (layoutToDelete: string) => {
    if (!shopId) return;

    try {
      await confirm({
        title: "Are you sure?",
        description:
          "This will remove the layout and cannot be undone. Are you sure you want to delete this layout?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return;
    }
    const layoutNames = layouts.map((layout) => Object.keys(layout)[0]);
    const defaultName =
      layoutNames.filter((c) => c !== layoutToDelete)[0] || "";

    try {
      setIsEditing(false);
      await layoutRepository.deleteLayout(shopId, layoutToDelete);
      showSuccessToast(
        "Layout Deleted",
        "The layout has been deleted successfully."
      );
      updateLayoutParam(defaultName);
      setNewLayoutName(defaultName);
      setTimeout(() => setIsEditing(true), 2000);
    } catch (error) {
      console.log(error);
      showErrorToast(
        "Error Deleting Layout",
        "There was an error deleting the layout. Please try again."
      );
      setTimeout(() => setIsEditing(true), 2000);
      return;
    }
  };

  const handleLayoutRename = async (renamedTo: string) => {
    if (!shopId || newLayoutName === "" || renamedTo === newLayoutName) return;

    const renamedFrom = newLayoutName;

    try {
      // Same handoff as delete: drop out of editing so the realtime
      // `layouts` update is allowed to land in `localLayouts` under the
      // layout's new key.
      setIsEditing(false);
      await layoutRepository.renameLayout(shopId, renamedFrom, renamedTo);

      // renameLayout migrates the shop's `layoutAccess` array server-side,
      // but layoutAccessArray is fetched via a separate, independently
      // cached query -- without invalidating it here, it keeps serving the
      // old layout name and a later access save would write that stale
      // array back over the migrated one.
      invalidateLayoutAccessQuery();

      // `dashboardLayoutId` stores the *name* of the layout this user has
      // selected on the dashboard, so a rename would otherwise leave it
      // pointing at a layout that no longer exists -- DashboardHeader then
      // silently bounces the user to whichever layout sorts first.
      if (dashboardLayoutId === renamedFrom) {
        setDashboardLayoutId(renamedTo);
      }

      updateLayoutParam(renamedTo);
      showSuccessToast(
        "Layout Renamed",
        "The layout has been renamed successfully."
      );
      setTimeout(() => setIsEditing(true), 2000);
    } catch (error) {
      console.log(error);
      showErrorToast(
        "Error Renaming Layout",
        "There was an error renaming the layout. Please try again."
      );
      setTimeout(() => setIsEditing(true), 2000);
    }
  };

  const saveLayout = async (layoutToSave: string, layoutData?: Layout[]) => {
    if (!shopId) return;

    try {
      const { created, updated } = await layoutRepository.updateLayout(
        shopId,
        layoutToSave,
        layoutData ?? layout.map((l) => toLayout(l))
      );

      setIsEditing(false);
      if (created) {
        showSuccessToast(
          "Layout Created",
          "The layout has been created successfully."
        );
      } else if (updated) {
        showSuccessToast(
          "Layout Updated",
          "The layout has been updated successfully."
        );
      }

      setTimeout(() => setIsEditing(true), 2000);
    } catch (error) {
      console.log(error);
      showErrorToast(
        "Error Saving Layout",
        "There was an error saving the layout. Please try again."
      );

      setTimeout(() => setIsEditing(true), 2000);
    }
    return;
  };

  const regenerateLayoutAccessArray = (
    layoutName: string,
    updatedAllowedList: string[]
  ) => {
    const updatedLayoutAccessArray = clone(layoutAccessArray || []);
    const layoutIndex = updatedLayoutAccessArray.findIndex(
      (access) => access.layoutName === layoutName
    );

    if (layoutIndex >= 0) {
      updatedLayoutAccessArray[layoutIndex].allowedList = updatedAllowedList;
    } else {
      updatedLayoutAccessArray.push({
        layoutName,
        allowedList: updatedAllowedList,
      });
    }

    return updatedLayoutAccessArray;
  };

  const handleLayoutAccessUpdate = async (
    layoutName: string,
    updatedAllowedList: string[]
  ) => {
    if (!shopId) return;

    const newLayoutAccessArray = regenerateLayoutAccessArray(
      layoutName,
      updatedAllowedList
    );

    // Use React Query mutation for automatic refetch
    try {
      await updateLayoutAccessMutation.mutateAsync(newLayoutAccessArray);
      showSuccessToast(
        "Layout Access Updated",
        "The layout access has been updated successfully."
      );
    } catch (error) {
      console.error("Failed to update layout access:", error);
      showErrorToast(
        "Error Updating Layout Access",
        "There was an error updating the layout access. Please try again."
      );
    }
  };

  const handleCreateContainer = async (container: Partial<Container>) => {
    if (!container.displayName) return;
    try {
      const newContainer = await containerRepository.createContainer(
        container.displayName,
        container.linkedTechnicianId,
        container.linkedSourceLabel,
        undefined,
        container.bidirectionalTechnicianId
      );
      if (!newContainer) {
        showErrorToast(
          "Error Creating Container",
          "There was an error creating the container. Please try again."
        );
        return;
      }
      await handleAddContainerToLayout(newContainer.id);
      showSuccessToast(
        "Container Created",
        "The container has been created and added to the layout."
      );
    } catch (error) {
      console.log(error);
      showErrorToast(
        "Error Creating Container",
        "There was an error creating the container. Please try again."
      );
    }
  };

  const handleToggleBulkEdit = () => {
    setBulkEditMode((prev) => !prev);
    setSelection([]);
  };

  const handleExitBulkEdit = () => {
    setBulkEditMode(false);
    setSelection([]);
  };

  const handleClearSelection = () => setSelection([]);

  const handleEditLinks = () => setBulkLinkOpen(true);

  // BulkLinkDialog closes itself after an Apply settles; the selection was
  // the dialog's input, so drop it (mirrors handleApplyBulkLabel).
  const handleLinksApplied = () => setSelection([]);

  const handleContainerSelect = (id: string) => {
    setSelection((prev) => toggleContainerSelection(prev, id));
  };

  // Resolves true only when the label was really written, so the action bar
  // keeps the chosen label (and the selection stays) on failure for a retry.
  const handleApplyBulkLabel = async (labelToApply: string) => {
    setIsApplyingBulkLabel(true);
    try {
      // One transaction that merges against fresh data, so a partial write
      // is impossible and no label added elsewhere meanwhile is overwritten.
      const updated = await containerRepository.addLabelToContainers(
        selectedInLayout,
        labelToApply
      );
      if (updated === 0) {
        showErrorToast(
          "No Containers Updated",
          "None of the selected containers could be updated. They may have been removed. Please try again."
        );
        return false;
      }
      showSuccessToast(
        "Label Applied",
        `Applied "${labelToApply}" to ${updated} container${
          updated === 1 ? "" : "s"
        }.`
      );
      setSelection([]);
      return true;
    } catch (error) {
      console.log(error);
      showErrorToast(
        "Error Applying Label",
        "There was an error applying the label to the selected containers. Please try again."
      );
      return false;
    } finally {
      setIsApplyingBulkLabel(false);
    }
  };

  const handleSaveLayout = (name: string, newLayout?: boolean) => {
    saveLayout(
      name,
      newLayout ? layoutRepository.generateBaseLayout() : undefined
    ).then(() => handleSelectLayout(name));
  };

  const handleImportLayout = async (
    layoutData: Layout[],
    layoutName: string,
    containers: Container[]
  ) => {
    if (!shopId) return;
    const currentContainers = shopData?.containers ?? [];
    try {
      // Validate the imported layout data here
      if (!Array.isArray(layoutData)) {
        throw new Error("Invalid layout format");
      }

      for (const container of containers) {
        if (!currentContainers.find((c) => c.id === container.id)) {
          await containerRepository.createContainer(
            container.displayName ?? container.id,
            [],
            [],
            container.id
          );
        }
      }

      // Create a new layout with the imported data
      const newLayout = await layoutRepository.addLayout(
        shopId,
        layoutName,
        layoutData
      );

      // Update the current layout and name
      setLayout(newLayout);
      updateLayoutParam(layoutName);

      // You might need to refresh layouts from the server or update the local store
      // depending on how your app manages state
    } catch (error) {
      console.error("Failed to import layout:", error);
      showErrorToast("Failed to import layout: Invalid format");
    }
  };

  return (
    <div className="p-2">
      <Dialog />
      <p>
        Having trouble with your layout?{" "}
        <SupportLink className={"text-red underline"}>Contact</SupportLink> a
        Layout Hero.
      </p>
      <div className="mt-4 flex w-full flex-row items-end justify-between gap-4 px-8">
        {/* Layout Management */}
        <div className="flex flex-row items-end gap-2">
          <div className="mr-8">
            <LayoutNameDialog
              layoutName={""}
              existingLayoutNames={layouts.map(Object.keys).flat()}
              onConfirm={(name) => handleSaveLayout(name, true)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Layout</Label>
            <Select
              onValueChange={handleSelectLayout}
              value={newLayoutName || "placeholder"} // Use "placeholder" if no layout is selected
            >
              <SelectTrigger className="h-12 w-60 text-primary">
                <SelectValue placeholder="Select Existing Layout" />
              </SelectTrigger>
              <SelectContent className="rounded-md border border-black shadow-lg">
                <SelectItem
                  value="placeholder"
                  disabled
                  className="text-gray-400"
                >
                  Select Existing Layout
                </SelectItem>
                {layouts.map((obj) => {
                  const name = Object.keys(obj)[0];
                  return (
                    <SelectItem
                      key={name}
                      value={name} // Use the name as the value
                      className="text-primary hover:bg-blue-500 hover:text-white"
                    >
                      {name} {/* Display the name */}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <Button
            disabled={newLayoutName?.length === 0}
            onClick={() => handleLayoutDelete(newLayoutName)}
            size="lgIcon"
            variant={"destructive"}
            className={"disabled:bg-zinc-700"}
          >
            <Trash2 />
          </Button>
          <LayoutNameDialog
            // Remount on layout switch: LayoutNameDialog seeds its form
            // through `useForm`'s defaultValues, which are only read on
            // mount -- without this the field keeps prefilling whichever
            // layout happened to be selected first.
            key={`rename-${newLayoutName}`}
            type={"edit"}
            layoutName={newLayoutName}
            // The layout's own name is not a collision with itself.
            existingLayoutNames={layouts
              .map(Object.keys)
              .flat()
              .filter((name) => name !== newLayoutName)}
            onConfirm={handleLayoutRename}
          />
          <LayoutNameDialog
            // Remounted per layout for the same reason as the rename
            // dialog above -- otherwise the "Copy of <name>" default keeps
            // naming whichever layout was selected at mount.
            key={`copy-${newLayoutName}`}
            type={"copy"}
            layoutName={newLayoutName}
            existingLayoutNames={layouts.map(Object.keys).flat()}
            onConfirm={handleSaveLayout}
          />
          <LayoutAccessDialog
            layoutAccess={layoutAccess}
            onConfirm={(updatedAllowedList) => {
              handleLayoutAccessUpdate(newLayoutName, updatedAllowedList);
            }}
            disabled={newLayoutName.length === 0}
          />
          {newLayoutName && (
            <Tooltip message={`Save ${newLayoutName}`}>
              <Button
                disabled={newLayoutName?.length === 0 || !isEditing}
                onClick={() => saveLayout(newLayoutName)}
                variant={"success"}
                size={"lgIcon"}
              >
                <SaveIcon />
              </Button>
            </Tooltip>
          )}

          <ImportExportLayout
            currentLayout={
              newLayoutName.length === 0
                ? undefined
                : layout.map((l) => toLayout(l))
            }
            containers={shopData?.containers ?? []}
            existingLayoutNames={layouts.map(
              (layout) => Object.keys(layout)[0]
            )}
            onImport={handleImportLayout}
          />
        </div>

        <div className={"flex-grow"} />
        <Link to={`/${shopId}/layouts`}>
          <Button variant={"accent"} size={"xl"}>
            <LayoutTemplateIcon className={"mr-2"} />
            Layout Order
          </Button>
        </Link>
        <Link to={`/${shopId}/layoutBuilder/containers`}>
          <Button variant={"accent"} size={"xl"}>
            <AppWindowMacIcon className={"mr-2"} />
            Manage Containers
          </Button>
        </Link>
        {isBulkEditAvailable && (
          <Button
            variant={bulkEditMode ? "accent" : "outline"}
            size={"xl"}
            onClick={handleToggleBulkEdit}
            disabled={!newLayoutName || !isEditing}
          >
            <ListChecks className={"mr-2"} />
            {bulkEditMode ? "Exit Bulk Edit" : "Bulk Edit"}
          </Button>
        )}
      </div>
      {newLayoutName && (
        <div className="m-2.5 mx-4 mt-4 flex flex-col gap-4 rounded-xl border border-primary-foreground p-5">
          <div className="flex flex-row items-center justify-between gap-2">
            <div className="flex-1 text-2xl" data-testid="active-layout-name">
              {newLayoutName !== "" ? newLayoutName : undefined}
            </div>
            {isEditing ? (
              <div className={"flex flex-row items-center gap-2"}>
                <ContainerDialog
                  employees={employees}
                  labels={labels}
                  labelCatalog={shopData?.labelCatalog}
                  onSubmit={handleCreateContainer}
                  containers={shopData?.containers}
                  integrationName={integrationName}
                  technicianWritebackEnabled={technicianWritebackEnabled}
                  trigger={
                    <Button variant={"accent"}>
                      <Tooltip message={"Create a new container"} asChild>
                        <PlusIcon />
                      </Tooltip>
                    </Button>
                  }
                />
                <MultiSelect
                  className={"w-60"}
                  placeholder={"Add Existing Containers"}
                  options={sortedContainerOptions}
                  value={currentLayoutContainers}
                  onChange={handleContainersChange}
                  itemsName={"containers"}
                  searchable
                />
              </div>
            ) : (
              <div className={"flex flex-row items-center gap-2"}>
                <Skeleton className={"h-8 w-8"} />
                <Skeleton className={"h-8 w-32"} />
              </div>
            )}
          </div>
          {bulkEditMode && (
            <>
              <BulkEditActionBar
                selectedCount={selectedInLayout.length}
                labels={labels}
                onApplyLabel={handleApplyBulkLabel}
                onEditLinks={
                  isBulkLinkEditAvailable ? handleEditLinks : undefined
                }
                onClearSelection={handleClearSelection}
                onExit={handleExitBulkEdit}
                applying={isApplyingBulkLabel}
              />
              {isBulkLinkEditAvailable && (
                <BulkLinkDialog
                  containerIds={selectedInLayout}
                  open={bulkLinkOpen}
                  onOpenChange={setBulkLinkOpen}
                  onApplied={handleLinksApplied}
                />
              )}
            </>
          )}
          <div className="h-[80vh] overflow-y-auto border border-primary-foreground bg-slate-950">
            <GridLayout
              layout={layout}
              onLayoutChange={onLayoutChange}
              removeItem={removeItem}
              editable={isEditing}
              bulkEditMode={bulkEditMode}
              selectedContainerIds={selectedInLayout}
              onContainerSelect={handleContainerSelect}
            />
          </div>
        </div>
      )}
    </div>
  );
};
